import React, { useState, useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import './AuditList.css'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import Support from '../contact-popup-module/Support'
import { Dialog, DialogContent, DialogTitle, Typography } from '@mui/material'
import CircularProgress from '@material-ui/core/CircularProgress'
import moment from 'moment'
import ShareAuditOptions from '../audit-creation-module/inspection-mode/ShareAuditOptions'
import { useSwipeable } from 'react-swipeable'
import { saveAs } from 'file-saver'

const AuditList = () => {
  const share = `${process.env.PUBLIC_URL}/custom-icons/share.svg`
  const downloadIcon = `${process.env.PUBLIC_URL}/custom-icons/download.svg`
  const calendar = `${process.env.PUBLIC_URL}/custom-icons/calendar.png`
  const menuIcon = `${process.env.PUBLIC_URL}/custom-icons/menu-icon.png`
  const infoIcon = `${process.env.PUBLIC_URL}/custom-icons/info-icon.png`
  const copy = `${process.env.PUBLIC_URL}/custom-icons/pink-copy-icon.png`
  const searchIcon = `${process.env.PUBLIC_URL}/custom-icons/search.svg`
  const next = `${process.env.PUBLIC_URL}/custom-icons/next-arrow.png`
  const upArrow = `${process.env.PUBLIC_URL}/custom-icons/upArrow.svg`
  const downArrow = `${process.env.PUBLIC_URL}/custom-icons/downArrow.svg`

  const location = useLocation()
  const insuranceUser = JSON.parse(localStorage.getItem('insuranceUser'))
  const userID = location.state ? location.state.userId : insuranceUser?.userId
  // const [activeTab, setActiveTab] = useState('new');
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'new')
  const [currentFilter, setCurrentFilter] = useState('all')
  const [isPopupVisible, setIsPopupVisible] = useState(false)
  const popupRef = useRef(null)
  const navigate = useNavigate()
  const [audits, setAudits] = useState([])
  const [agentDetails, setAgentDetails] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [open, setOpen] = useState(false)
  const [openSharePopup, setOpenSharePopup] = useState(false)
  const [selectedAuditUrl, setSelectedAuditUrl] = useState('')
  const [dialogWidth, setDialogWidth] = useState('29vw')

  const [isAscending, setIsAscending] = useState(false)

  const [audit_status, setAuditStatus] = useState(null)
  const [formDetailsMap, setFormDetailsMap] = useState({})
  const [allInfoLoaded, setAllInfoLoaded] = useState(false)
  const [loadingComplete, setLoadingComplete] = useState(false)
  const [counts, setCounts] = useState({ new: 0, inProgress: 0, inactive: 0 })
  const [sortedAudits, setSortedAudits] = useState([])
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab)
    }
  }, [location.state])

  useEffect(() => {
    const fetchAuditsAndDetails = async () => {
      setLoading(true);

      try {
        if (!userID) {
          console.error('User ID is missing');
          setLoading(false);
          return;
        }

        // Fetch audits and agent info in parallel
        const [auditResponse, agentResponse] = await Promise.all([
          axios.post('/agentApis/getAgentAllAuditInfo', { userID }),
          axios.post('/agentApis/getCompleteAgentInfo', { userID })
        ]);

        console.log('Audits by user:', auditResponse.data, agentResponse.data);

        // Map initial audit data
        let auditData = auditResponse.data.map(item => {
          const leadTime = item.lead_creation_time || '';
          const [date = '--', time = '--'] = leadTime.split(' ');

          return {
            id: item.audit,
            date,
            time,
            used: item.used,
            status: (() => {
              switch (item.used) {
                case 'created':
                case 'accessed':
                  return 'pendingVerification';
                case 'processing':
                case 'processed':
                case 'completed':
                case 'expired':
                case 'used':
                  return 'inactive';
                default:
                  return 'unknown';
              }
            })(),
            type: ['Inspection'],
            mode: item.audit_mode
          };
        });

        console.log("Initial auditData length:", auditData.length);

        // Filter audits that need status verification
        const pendingVerificationAudits = auditData.filter(
          audit => audit.status === 'pendingVerification'
        );

        console.log("pendingVerificationAudits length:", pendingVerificationAudits.length);

        if (pendingVerificationAudits.length > 0) {
          const startTime1 = performance.now();

          const auditsPayload = pendingVerificationAudits.map(audit => ({
            id: audit.id,
            processed: 0,
            inspection_time: moment().format('DD-MM-YYYY HH:mm'),
            used: audit.used // needed for business logic
          }));

          try {
            const res = await axios.post('/create_audit/fetch_status_batch', {
              audits: auditsPayload
            });

            console.log('Batch status response:', res);

            const updatedStatuses = res.data?.updatedAudits || [];

            if (updatedStatuses.length === 0) {
              console.warn('No updated audits received');
            }

            const updatedStatusesMap = updatedStatuses.reduce((acc, audit) => {
              acc[audit.id] = audit;
              return acc;
            }, {});

            // Merge updated status with existing auditData
            auditData = auditData.map(audit => {
              if (audit.status !== 'pendingVerification') return audit;
              const updated = updatedStatusesMap[audit.id];
              return updated ? { ...audit, status: updated.status } : audit;
            });

            const endTime1 = performance.now();
            console.log(`Time taken to fetch batch status: ${(endTime1 - startTime1).toFixed(2)} ms`);
          } catch (err) {
            console.error('Error fetching batch status:', err.message);
          }
        }

        // Batch fetch audit details
        const auditIds = auditData.map(audit => audit.id);
        console.log("Total audits to fetch details for:", auditIds.length);

        const startTime = performance.now();

        const detailsResponse = await axios.post('/process_data/batch_check_and_get_details', {
          auditIds
        });

        const endTime = performance.now();
        console.log(`Time taken to fetch batch details: ${(endTime - startTime).toFixed(2)} ms`);

        const fetchedDetails = detailsResponse.data.audits;

        // Merge fetched details with auditData
        const auditDataWithDetails = auditData.map(audit => {
          const found = fetchedDetails.find(item => item.id === audit.id);
          return {
            ...audit,
            Make: found?.details.Make || '',
            Model: found?.details.Model || '',
            'Name of Customer': found?.details['Name of Customer'] || ' ',
            'Vehicle No': found?.details['Vehicle No'] || '',
            'Client Unique Reference No.': found?.details['Client Unique Reference No.'] || ''
          };
        });

        console.log("auditDataWithDetails:", auditDataWithDetails);

        // Compute status counts
        const computedCounts = auditDataWithDetails.reduce(
          (acc, audit) => {
            acc[audit.status] = (acc[audit.status] || 0) + 1;
            return acc;
          },
          { new: 0, inProgress: 0, inactive: 0 }
        );

        console.log("Computed counts:", computedCounts);

        // Update state
        setAudits(auditDataWithDetails);
        setCounts(computedCounts);

        if (agentResponse.status === 200) {
          setAgentDetails(agentResponse.data[0]);
        } else {
          setError('Failed to fetch agent details');
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('An error occurred while fetching data');
      } finally {
        setLoadingComplete(true);
        setLoading(false);
      }
    };

    fetchAuditsAndDetails();
  }, [userID]);

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth
      if (windowWidth <= 450) {
        setDialogWidth('94vw')
      } else if (windowWidth <= 475) {
        setDialogWidth('94vw')
      } else if (windowWidth <= 570) {
      } else if (windowWidth <= 575) {
        setDialogWidth('64vw')
      } else if (windowWidth <= 650) {
        setDialogWidth('60vw')
      } else if (windowWidth <= 700) {
      } else if (window.innerWidth <= 775) {
        setDialogWidth('57vw')
      } else if (window.innerWidth <= 875) {
        setDialogWidth('55vw')
      } else if (windowWidth <= 1000) {
        setDialogWidth('48vw')
      } else if (window.innerWidth <= 1100) {
        setDialogWidth('45vw')
      } else if (windowWidth <= 1300) {
        setDialogWidth('40vw')
      } else {
        setDialogWidth('35vw')
      }
    }

    window.addEventListener('resize', handleResize)
    handleResize()
    // Cleanup function to remove event listener
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleCopy = () => {
    navigator.clipboard
      .writeText(selectedAuditUrl)
      .then(() => {
        toast.success('Link Copied!', {
          position: 'bottom-center',
          autoClose: 1000,
          type: 'default',
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: false,
          draggable: true,
          className: 'custom-toast'
        })
      })
      .catch(err => {
        console.error('Failed to copy: ', err)
      })
  }
  const tabsRef = useRef({})
  // Tab switching handler
  // const [searchQuery, setSearchQuery] = useState("");

  const handleTabClick = tab => {
    setActiveTab(tab)
    // setSearchQuery(""); // Clear search query

    // Scroll the clicked tab into view
    tabsRef.current[tab]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })
  }


  const tabs = ['new', 'inProgress', 'inactive']

  const [swiping, setSwiping] = useState(false)

  const swipeHandlers = useSwipeable({
    onSwiping: () => setSwiping(true), // Detecting swipe
    onSwiped: () => setSwiping(false), // Reset after swipe
    onSwipedLeft: () => {
      const currentIndex = tabs.indexOf(activeTab)
      if (currentIndex < tabs.length - 1) {
        handleTabClick(tabs[currentIndex + 1])
      }
    },
    onSwipedRight: () => {
      const currentIndex = tabs.indexOf(activeTab)
      if (currentIndex > 0) {
        handleTabClick(tabs[currentIndex - 1])
      }
    },
    delta: 80, // Adjusts swipe sensitivity (higher = smoother)
    trackTouch: true, // Ensures touch tracking for smoothness
    preventScrollOnSwipe: true // Prevents page scroll on swipe
  })
  // Filter button handler
  const handleFilterClick = filter => {
    setCurrentFilter(filter)
  }

  // useEffect(() => {
  //     console.log("Filtered Audits:", filteredAudits);
  //     console.log("Form Details Map:", formDetailsMap);
  // }, [filteredAudits, formDetailsMap]);

  const handleClickOutside = event => {
    if (popupRef.current && !popupRef.current.contains(event.target)) {
      setIsPopupVisible(false)
    }
  }

  useEffect(() => {
    if (isPopupVisible) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isPopupVisible])
  const handleNavigateClick = audit => {
    // const userId = location.state.userId
    console.log(userID)
    navigate(`/auditDescription`, {
      state: { audit, userID, agentDetails, activeTab }
    })
  }
  const handleAddClick = () => {
    navigate('/home')
  }

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }
  const handleLogout = () => {
    navigate('/login')
  }
  const link_to_audit = audit => {
    console.log(audit?.mode, 187)
    const hostname = window.location.hostname
    const port = window.location.port
    const auditId = audit.id
    // const baseURL = `https://${hostname}:${port}`;
    const baseURL =
      port !== '' ? `https://${hostname}:${port}` : `https://${hostname}`
    // let url = `/audit/inspection/mode/${auditId}`;
    let url = `${baseURL}/audit/inspection/mode/${auditId}`

    // if (audit?.mode === "video") {
    //     url = `/audit/video/permission/${auditId}`;
    // } else if (audit?.mode === "photo") {
    //     url = `/audit/photo/permission/${auditId}`;
    // } else {
    //     url = `/audit/hybrid/permission/${auditId}`;
    // }
    return url
  }
  const handleShareClick = audit => {
    console.log('share clicked')

    const auditUrl = link_to_audit(audit) // Generate the audit URL
    setSelectedAuditUrl(auditUrl) // Set the URL for sharing
    setOpenSharePopup(true) // Open the share popup
  }

  const handleShareClose = () => {
    setOpenSharePopup(false) // Close the share popup
  }

  const [downloading, setDownloading] = useState({}) // Track downloading state per audit ID

  const handleDownloadClick = async audit_id => {
    setDownloading(prev => ({ ...prev, [audit_id]: true })) // Set downloading state

    try {
      // Prepare blob name based on audit_id
      const blobName = `Insurance_Inspection/downloadedPdf/${audit_id}.pdf`

      // Call the API to check if document exists and get signed URL
      const response = await fetch('/process_data/check_document_exists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ blobName })
      })

      const data = await response.json()
      console.log('452', data)
      if (data.exists && data.url) {
        // Open the signed URL in a new tab
        window.open(data.url, '_blank')
      } else {
        alert('Document does not exist in S3.')
      }
    } catch (error) {
      console.error('Error fetching signed URL:', error)
      alert('Failed to download the document. Please try again later.')
    } finally {
      setDownloading(prev => ({ ...prev, [audit_id]: false })) // Reset downloading state
    }
  }

  const [filteredAudit, setFilteredAudit] = useState([]) // Initialize with `audits`
  const filteredAudits = (
    filteredAudit.length > 0 ? filteredAudit : audits
  ).filter(audit => {
    return audit.status === activeTab
  })

  const handleSearch = query => {
    // setSearchQuery(query);

    const filtered = audits.filter(
      audit =>
        audit['Name of Customer'].toLowerCase().includes(query.toLowerCase()) ||
        audit['id'].toLowerCase().includes(query.toLowerCase())
    )
    setFilteredAudit(filtered)
  }

  const [sortOrder, setSortOrder] = useState('desc') // Default: Descending

  const handleDateSort = () => {
    setSortOrder(prevOrder => (prevOrder === 'asc' ? 'desc' : 'asc'))
  }
  console.log('activeTab', activeTab)

  return (
    <div className='audit-list-container'>
      <div className='header-container'>
        <h1>Vehicle Inspection</h1>
        {/* <h1>Assigned audits</h1> */}
        <img
          src={menuIcon}
          alt='Options'
          className='three-dots-icon'
          onClick={() => setIsPopupVisible(!isPopupVisible)}
        />
        {isPopupVisible && (
          <div className='popup-menu' ref={popupRef}>
            <button className='popup-item' onClick={handleClickOpen}>
              <img src={infoIcon} alt='Info' className='info-icon' />
              Help
            </button>
            <button className='popup-item' onClick={handleLogout}>
              Logout
            </button>
          </div>
        )}
      </div>
      {/* Support Popup */}
      <Dialog
        open={open}
        onClose={handleClose}
        className='support-popup-dialog'
      >
        <DialogContent className='support-popup-content'>
          <Support />
          <button onClick={handleClose} className='close-btn'>
            close
          </button>
        </DialogContent>
      </Dialog>

      <div className='tabs-container'>
        <div className='tabs'>
          <button
            className={activeTab === 'new' ? 'active' : ''}
            onClick={() => handleTabClick('new')}
            ref={el => (tabsRef.current['new'] = el)}
          >
            NEW <span className='count'>{counts.new}</span>
          </button>
          <button
            className={activeTab === 'inProgress' ? 'active' : ''}
            onClick={() => handleTabClick('inProgress')}
            ref={el => (tabsRef.current['inProgress'] = el)}
          >
            IN PROGRESS <span className='count'>{counts.inProgress}</span>
          </button>
          <button
            className={activeTab === 'inactive' ? 'active' : ''}
            onClick={() => handleTabClick('inactive')}
            ref={el => (tabsRef.current['inactive'] = el)}
          >
            COMPLETED <span className='count'>{counts.inactive}</span>
          </button>
        </div>
      </div>

      <div className='filters'>
        <div className='search-container'>
          <input
            type='text'
            placeholder='Search'
            className='search-input'
            // value={searchQuery} // Bind to state
            onChange={e => handleSearch(e.target.value)}
          />
          <img src={searchIcon} alt='Search Icon' className='search-icon' />
        </div>

        {/* <button className="date-filter-button" onClick={() => handleDateSort()}>
                    Date
                    <span className="sort-icon">
                        <img src={downArrow} alt="Sort Descending" className="sort-arrow down-arrow" />
                        <img src={upArrow} alt="Sort Ascending" className="sort-arrow up-arrow" />
                    </span>
                </button> */}
        {!(activeTab === 'new' || activeTab === 'inProgress') && (
          <button className='date-filter-button' onClick={handleDateSort}>
            Date
            <span className='sort-icon'>
              <img
                src={downArrow}
                alt='Sort Descending'
                className={`sort-arrow down-arrow ${sortOrder === 'desc' ? 'visible' : 'hidden'
                  }`}
              />
              <img
                src={upArrow}
                alt='Sort Ascending'
                className={`sort-arrow up-arrow ${sortOrder === 'asc' ? 'visible' : 'hidden'
                  }`}
              />
            </span>
          </button>
        )}
      </div>

      {!loadingComplete ? (
        <div className='loader-container'>
          <CircularProgress style={{ color: '#ff0083' }} size={40} />
        </div>
      ) : audits.length === 0 ? (
        <div className='no-audit-message'>
          {/* No audit created by this ID */}
        </div>
      ) : (
        <div
          {...swipeHandlers}
          className={`audit-cards ${swiping ? 'swiping' : ''}`}
        >
          {
            // filteredAudits
            //     .slice()
            //     .sort((a, b) => {
            //         const dateA = moment(`${a.date} ${a.time}`, "DD-MM-YYYY HH:mm").toDate();
            //         const dateB = moment(`${b.date} ${b.time}`, "DD-MM-YYYY HH:mm").toDate();

            //         // Compare combined date and time
            //         return dateB - dateA; // Sort descending by date and time
            //     })
            filteredAudits
              .slice()
              .sort((a, b) => {
                const dateA = moment(
                  `${a.date} ${a.time}`,
                  'DD-MM-YYYY HH:mm'
                ).toDate()
                const dateB = moment(
                  `${b.date} ${b.time}`,
                  'DD-MM-YYYY HH:mm'
                ).toDate()

                return sortOrder === 'asc' ? dateA - dateB : dateB - dateA
              })
              .map(audit => (
                <div className='audit-card' key={audit.id}>
                  <div className='audit-header'>
                    <div className='header-one'>
                      <p className='audit-id'>{audit.id}</p>
                      <div className='date-container'>
                        <img
                          src={calendar}
                          alt='Calendar Icon'
                          className='calendar-icon'
                        />
                        <p>{audit.date}</p>
                      </div>
                    </div>
                    <div className='header-two'>
                      {/* <div className="audit-tags">
                                                {audit.mode && (
                                                    <span className="audit-tag">
                                                        {audit.mode.toUpperCase()} MODE
                                                    </span>
                                                )}
                                            </div> */}
                      <div className='audit-tags'>
                        {audit.mode && (
                          <span className='audit-tag'>
                            {audit.mode.charAt(0).toUpperCase() +
                              audit.mode.slice(1).toLowerCase()}{' '}
                            Mode
                          </span>
                        )}
                      </div>

                      <p>{audit.time}</p>
                    </div>
                  </div>
                  <div className='audit-details'>
                    <h4>{audit['Name of Customer']}</h4>
                    <p>
                      Client Reference Number :
                      {audit['Client Unique Reference No.'] || ''}
                    </p>
                    <p>
                      Vehicle Make/ Model : {audit.Make || ''}{' '}
                      {audit.Model || ''}
                    </p>
                    <p>Vehicle Number : {audit['Vehicle No']}</p>
                  </div>

                  <div className='audit-actions'>
                    {activeTab === 'inactive' ? (
                      audit.used === 'processed' ? (
                        <button
                          className='download-button'
                          onClick={() => handleDownloadClick(audit.id)}
                          disabled={downloading[audit.id]} // Disable only the clicked button
                        >
                          {downloading[audit.id]
                            ? 'Downloading...'
                            : 'Download'}
                          <img
                            src={downloadIcon}
                            alt='Download Icon'
                            className='download-icon'
                          />
                        </button>
                      ) : audit.used === 'processing' ? (
                        <button className='expired-button' disabled>
                          Used
                        </button>
                      ) : (
                        <button className='expired-button' disabled>
                          Expired
                        </button>
                      )
                    ) : (
                      <button
                        className='share-button'
                        onClick={() => handleShareClick(audit)}
                      >
                        <img
                          src={share}
                          alt='Share Icon'
                          className='share-icon'
                        />
                        Share
                      </button>
                    )}

                    <button
                      className='navigate-button'
                      onClick={() => handleNavigateClick(audit)}
                    >
                      {activeTab === 'inactive' ? 'More Info' : 'Start'}
                      <img src={next} alt='next Icon' className='next-icon' />
                    </button>
                  </div>
                </div>
              ))
          }
        </div>
      )}
      <button className='fab' onClick={() => handleAddClick()}>
        +
      </button>
      {/* Share Audit Popup */}
      <div style={{ position: 'relative' }}>
        <ToastContainer />
        <Dialog
          open={openSharePopup}
          onClose={handleShareClose}
          maxWidth='150vw'
          maxHeight='lg'
          PaperProps={{
            style: {
              width: dialogWidth,
              // padding: "1.5rem 1rem",
              paddingBottom: '0.5rem',
              borderRadius: '4px',
              margin: '0px',
              // height: "242px",
              zIndex: '999999'
            }
          }}
        >
          <DialogTitle
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: '1rem',
              fontSize: '1.7rem',
              textAlign: 'center',
              color: 'black',
              // padding: "2rem 0.6rem 1rem 0.6rem",
              fontWeight: '600',
              fontFamily: 'Barlow Condensed'
            }}
          >
            <Typography
              variant='body2'
              color='textSecondary'
              style={{ fontSize: '0.9rem' }}
            >
              Share Inspection link via
            </Typography>
            <ShareAuditOptions auditUrl={selectedAuditUrl} />
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.2rem',
                border: '1.5px solid #FF0083',
                padding: '0.2rem 1.2rem',
                backgroundColor: 'white',
                borderRadius: '5px',
                width: '100%',
                cursor: 'pointer'
              }}
              onClick={handleCopy}
            >
              <img
                src={copy}
                style={{ width: '1rem', height: '1rem' }}
                alt='Share Icon'
              />
              <p
                style={{
                  fontSize: '1rem',
                  fontFamily: "'Montserrat', sans-serif",
                  color: '#FF0083',
                  padding: '0.5rem',
                  cursor: 'pointer'
                }}
              >
                Copy url link
              </p>
            </button>
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.2rem',
                // border: "1.5px solid #FF0083",
                padding: '0.4rem 1rem',
                backgroundColor: '#FF0083',
                borderRadius: '5px',
                width: '40%',
                cursor: 'pointer'
              }}
              onClick={handleShareClose}
            >
              <span
                style={{
                  fontSize: '1rem',
                  fontFamily: "'Montserrat', sans-serif",
                  color: 'white',
                  padding: '0.2rem',
                  cursor: 'pointer'
                }}
              >
                Close
              </span>
            </button>
          </DialogTitle>
        </Dialog>
      </div>
    </div>
  )
}

export default AuditList
